import { NgIf, UpperCasePipe } from '@angular/common';
import { Component, input, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { combineLatest, startWith } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FormIoWidgetService, IFilterValuesVisibleToWidget, ISubmissionByFilters } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { UtcToUserFormattedPipe } from '../../../core/pipes/utc-to-user-formatted.pipe';
import { DatatableComponent } from '../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../shared/components/datatable/datatable.interface';
import { FilterCardComponent } from '../../../shared/components/filter-card/filter-card.component';
import {
  FormioDialogComponent,
  IFormioDialogData,
} from '../../../shared/components/formio-dialog/formio-dialog.component';
import { UserChipComponent } from '../../../shared/components/user-chip/user-chip.component';
import { ELoadStatus } from '../../../state/state.interface';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameterFilters,
} from '../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { IFormSubmissionData } from './form-submissions.interface';
import { FormSubmissionsStore, IFormSubmissionsComponentState } from './form-submissions.store';

@Component({
  imports: [
    DatatableComponent,
    MatCard,
    MatCardContent,
    ReactiveFormsModule,
    TranslateModule,
    MatButton,
    MatChip,
    NgIf,
    UpperCasePipe,
    UtcToUserFormattedPipe,
    UserChipComponent,
    FilterCardComponent,
  ],
  providers: [FormSubmissionsStore, HttpUtilitiesService],
  selector: 'app-form-submissions',
  standalone: true,
  styleUrls: ['./form-submissions.component.scss'],
  templateUrl: './form-submissions.component.html',
})
export class FormSubmissionsComponent
  extends BaseCrudPageComponent<IFormSubmissionData, IFormSubmissionsComponentState>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @Input() dashboardId?: number;
  @Input() widgetId?: number;
  public readonly formId = input<number | null>(null);
  @Input() hideFilterCard?: boolean;
  @Input() advancedFilters?: IFilterValuesVisibleToWidget[] | undefined;
  private submissionFilters: ISubmissionByFilters | null = null;
  private dateFilters: IGenericCrudRequestConstructionParameterFilters[] = [];

  public datatableConfigurations: IDatatableConfiguration<IFormSubmissionData> = {
    addButtonText: null,
    addScope: ETableCrudScope.none,
    areCheckboxesEnabled: false,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.none,
    editScope: ETableCrudScope.none,
  };

  public readonly datatableColumns: IDatatableColumn<IFormSubmissionData>[] = [
    { id: 'id', isSortable: false },
    { id: 'dashboard', isSortable: true },
    { id: 'form', isSortable: true },
    { id: 'formAssociation', isSortable: false },
    { id: 'submittedAt', isSortable: true },
    { id: 'submittedBy', isSortable: true },
    { id: 'actions', isSortable: false },
  ];

  constructor(
    public readonly store: FormSubmissionsStore,
    public readonly formService: FormUtilitiesService,
    private readonly formIoWidgetService: FormIoWidgetService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfExample',
      multiContext: 'field.formSubmissions',
      nameProperty: 'form',
      singleContext: 'field.formSubmission',
    });
  }

  public ngOnInit(): void {
    if (this.advancedFilters?.length) {
      this.dateFilters = [];
      this.submissionFilters = this.formIoWidgetService.mapFiltersToInterface(
        this.advancedFilters,
        this.dateFilters,
        'generic',
        this.store,
      );
    }

    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        extraShiftIds: this.store.currentPreviousShiftsForFilter$.pipe(startWith([])),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
        loadStatus: this.store.loadFilterData$.pipe(filter((status) => status !== ELoadStatus.loading)),
      }).subscribe(({ datatable, extraShiftIds, filterCard }) => {
        if (extraShiftIds?.length && this.submissionFilters) {
          const currentIds: number[] = this.submissionFilters.shiftIds || [];
          this.submissionFilters.shiftIds = [...new Set([...currentIds, ...extraShiftIds])];
        }

        const sortField: string | undefined = this.mapSortField(datatable.sort?.active);

        const filters: IGenericCrudRequestConstructionParameterFilters[] = [];

        if (this.dashboardId) {
          filters.push({ field: 'dashboardId', ids: [this.dashboardId] });
        }

        if (this.widgetId) {
          filters.push({ field: 'widgetId', ids: [this.widgetId] });
        }

        if (this.dateFilters?.length) {
          filters.push(...this.dateFilters);
        }

        this.store.loadFormSubmissions({
          additionalSearch: this.submissionFilters ? JSON.stringify(this.submissionFilters) : undefined,
          params: {
            fields: [
              'formId',
              'widgetId',
              'dashboardId',
              'submission',
              'configuration',
              'createdAt',
              'dashboard||name',
              'form||form,name',
              'user||name',
            ],
            filters: [
              ...filters,
              ...(!isNil(filterCard.dashboard) ? [{ field: 'dashboard.id', ids: [filterCard.dashboard] }] : []),
              ...(!isNil(filterCard.form) ? [{ field: 'form.id', ids: [filterCard.form] }] : []),
              ...(!isNil(this.formId()) ? [{ field: 'formId', ids: [this.formId()!] }] : []),
            ],
            join: ['form', 'user', 'dashboard'],
            page: datatable.page,
            sort: sortField ? [{ active: sortField, direction: datatable.sort.direction }] : undefined,
            ...(datatable.search
              ? { search: { searchedFields: ['form.name', 'dashboard.name'], searchText: datatable.search } }
              : {}),
            limit: datatable.limit,
          },
        });
      }),
    );
  }

  public onView(submission: IFormSubmissionData): void {
    this.matDialog.open(FormioDialogComponent, {
      data: {
        form: submission.formioForm,
        submission: submission.submission,
        title: this.translate.instant('field.formSubmission'),
      } as IFormioDialogData,
      disableClose: false,
      maxWidth: '100%',
      restoreFocus: false,
      width: EDialogWidth.xl,
    });
  }

  private mapSortField(frontendField?: string): string | undefined {
    if (!frontendField) {
      return undefined;
    }

    if (frontendField === 'dashboard') {
      return 'dashboard.name';
    } else if (frontendField === 'form') {
      return 'form.name';
    } else if (frontendField === 'submittedAt') {
      return 'createdAt';
    } else if (frontendField === 'submittedBy') {
      return 'user.name';
    }

    return frontendField;
  }
}
